import { useEffect } from 'react';

/**
 * Handles hash-based scrolling and state updates on **initial page load only**.
 *
 * This hook exists because the app uses `scroll-behavior: smooth` globally.
 * When entering a page directly with a hash (e.g. `#citation-list`), smooth
 * scrolling can land at the wrong position due to lazy-loaded content
 * changing the page height during the scroll.
 *
 * This hook combines two behaviors needed for initial hash handling:
 * 1. Expands the accordion if hash matches
 * 2. Scrolls to the target element with instant behavior (after DOM stabilizes)
 *
 * Note: This only runs on component mount. Hash changes from link clicks
 * are handled separately by the Citation component.
 */
export const useHashScrollOnMount = ({
  onExpand,
  hashKey,
}: {
  onExpand: () => void;
  hashKey: string;
}) => {
  useEffect(() => {
    if (window.location.hash === `#${hashKey}`) {
      onExpand();

      window.scrollTo(0, 0);

      setTimeout(() => {
        const element = document.getElementById(hashKey);
        if (element) {
          element.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      }, 10);
    }
  }, [onExpand]);
};
